import React, { useEffect, useState } from "react";
import { Drawer, Form, Input, Select, Space, message, Skeleton, Empty, Flex } from "antd";
import { SaveOutlined } from "@ant-design/icons";
import CancelButton from "../common/cancelbutton";
import FormButton from "../common/formbutton";
import ImageWithSpinner from "../content/imagewithspinner";
import { getMenuTraits, getPageTraitByPageId, savePageTrait } from "../../services/pageservice";

const { Option } = Select;

interface Trait {
    value: string;
    label: string;
}

interface PageTraitDto {
    altName: string;
    altImageUrl: string;
    traitGuid: string;
}

interface DrawerPageTraitProps {
    drawerVisible: boolean;
    closeDrawer: () => void;
    isDisabled: boolean;
    pageGuid: string;
    pageversionGuid: string;
}

const DrawerPageTrait: React.FC<DrawerPageTraitProps> = ({
    drawerVisible,
    closeDrawer,
    isDisabled,
    pageGuid,
    pageversionGuid
}) => {
    const [form] = Form.useForm();
    const [traits, setTraits] = useState<Trait[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [previewImageUrl, setPreviewImageUrl] = useState<string>("");
    const [saving, setSaving] = useState<boolean>(false);

    const handleCloseDrawer = () => {
        form.resetFields();
        setPreviewImageUrl("");
        closeDrawer();
    };

    useEffect(() => {
        if (drawerVisible) {
            setLoading(true);

            const fetchData = async () => {
                try {
                    const traitData = await getMenuTraits();
                    const traitList = traitData.map((item: any) => ({
                        value: item.value,
                        label: item.label
                    }));
                    setTraits(traitList);

                    const pageTrait: PageTraitDto = await getPageTraitByPageId(pageGuid);                  

                    form.setFieldsValue({
                        altName: pageTrait.altName,
                        altImageUrl: pageTrait.altImageUrl,
                        pageTrait: pageTrait.traitGuid
                    });

                    setPreviewImageUrl(pageTrait.altImageUrl);
                } catch (error) {
                    console.error("Error loading trait data:", error);
                    message.error("Failed to load page trait data");
                } finally {
                    setLoading(false);
                }
            };

            fetchData();
        }
    }, [drawerVisible, form, pageGuid]);

    const handleSave = () => {
        form
            .validateFields()
            .then(async (values) => {
                setSaving(true);
                try {

                    const payload = {
                        pageGuid,
                        pageversionGuid,
                        altName: values.altName,
                        altImageUrl: values.altImageUrl,
                        traitGuid: values.pageTrait
                    };

                    await savePageTrait(payload);

                    message.success("Page trait saved successfully");
                    handleCloseDrawer();
                } catch (error: any) {
                    console.error("Save error:", error);
                    message.error("Failed to save page trait");
                } finally {
                    setSaving(false);
                }
            })
            .catch(() => {
                message.error("Please fix validation errors");
            });
    };

    const footer = (
        <div style={{ textAlign: "right" }}>
            {!isDisabled && (
                <Space>
                    <CancelButton onClick={handleCloseDrawer} />
                    <FormButton
                        title="Save"
                        icon={<SaveOutlined />}
                        onClick={handleSave}
                        loading={saving}
                    />
                </Space>
            )}
        </div>
    );

    return (
        <Drawer
            title="Page Trait"
            placement="right"
            onClose={handleCloseDrawer}
            open={drawerVisible}
            width={400}
            styles={{
                header: {
                    color: "white",
                    backgroundColor: "var(--color-primary)",
                    fontWeight: 350,
                    fontSize: "14px",
                    border: 0,
                    minHeight: "40px"
                }
            }}
            footer={footer}
        >
            {loading ? (
                <Skeleton active paragraph={{ rows: 4 }} />
            ) : (
                <>
                    <Flex justify="center" style={{ marginBottom: "10px" }}>
                        <div style={{ width: "180px", height: "180px" }}>
                            {previewImageUrl ? (
                                <ImageWithSpinner imageUrl={previewImageUrl} />
                            ) : (
                                <Empty description="Image Not Added" />
                            )}
                        </div>
                    </Flex>

                    <Form
                        layout="vertical"
                        form={form}
                        style={{ paddingBottom: 80 }}
                        onValuesChange={(changedValues) => {
                            if (changedValues.altImageUrl !== undefined) {
                                setPreviewImageUrl(changedValues.altImageUrl);
                            }
                        }}
                    >
                        <Form.Item
                            name="altName"
                            label="Alt Name"
                            rules={[{ required: true, message: "Please enter alt name" }]}
                        >
                            <Input placeholder="Enter alt name" disabled={isDisabled} />
                        </Form.Item>

                        <Form.Item
                            name="altImageUrl"
                            label="Alt Image URL"
                            rules={[{ required: true, message: "Please enter image URL" }]}
                        >
                            <Input placeholder="Enter image URL" disabled={isDisabled} />
                        </Form.Item>

                        <Form.Item
                            name="pageTrait"
                            label="Page Trait"
                            rules={[{ required: true, message: "Please select a trait" }]}
                        >
                            <Select placeholder="Select trait" disabled={isDisabled}>
                                {traits.map((trait) => (
                                    <Option key={trait.value} value={trait.value}>
                                        {trait.label}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Form>
                </>
            )}
        </Drawer>
    );
};

export default DrawerPageTrait;